import { useEffect, useMemo, useRef } from 'react';

import { getFilterValues, useTableFilters } from '@/hooks/useTableFilters';
import type {
  ScheduleCadence,
  Simulation,
  SimulationChannelType,
} from '@/modules/simulations/types';

// U+0000 (NULL) is a non-whitespace control character that the backend's sanitizedName
// pipeline strips from all user input. A stored category value can therefore never contain
// it, which makes this sentinel safe to use as an internal filter value without risking
// collision with a user-supplied category name like "__uncategorized__".
const UNCATEGORIZED_SENTINEL = '\u0000__uncategorized__\u0000';
export const UNSCHEDULED_SENTINEL = '\u0000__unscheduled__\u0000';

export const categoryToFilterValue = (category: string | null): string =>
  category === null ? UNCATEGORIZED_SENTINEL : category;

export const filterValueToCategory = (value: string): string | null =>
  value === UNCATEGORIZED_SENTINEL ? null : value;

export const scheduleFrequencyToFilterValue = (frequency: ScheduleCadence | null): string =>
  frequency === null ? UNSCHEDULED_SENTINEL : frequency;

export const useSimulationsFilters = (simulations: Simulation[]) => {
  const { filters, setFilter, removeFilter, resetFilters, activeFilterCount } = useTableFilters({
    storageKey: 'simulationsFilters',
  });

  // Hold the latest filters in a ref so the prune effect can read them without re-running
  // whenever filters change (which would be a loop, since the effect calls setFilter).
  const filtersRef = useRef(filters);
  filtersRef.current = filters;

  // Prune persisted category values that no longer exist in the data — e.g. when the
  // only simulation with a given category was deleted, or when a previously uncategorized
  // row was assigned a category. Without this, stale values get stuck in the filter
  // (hitting the sentinel fallback in badges and returning an empty list).
  useEffect(() => {
    if (simulations.length === 0) {
      return;
    }
    const persisted = getFilterValues(filtersRef.current, 'category');
    if (persisted.length === 0) {
      return;
    }
    const valid = new Set(simulations.map((s) => categoryToFilterValue(s.category)));
    const pruned = persisted.filter((v) => valid.has(v));
    if (pruned.length !== persisted.length) {
      setFilter('category', pruned);
    }
  }, [simulations, setFilter]);

  const filteredSimulations = useMemo(() => {
    if (!simulations.length) {
      return [];
    }

    const categories = getFilterValues(filters, 'category').map(filterValueToCategory);
    const channels = getFilterValues(filters, 'channel_type') as SimulationChannelType[];
    const schedules = getFilterValues(filters, 'schedule_frequency');

    return simulations.filter((sim) => {
      if (categories.length && !categories.includes(sim.category)) {
        return false;
      }
      if (channels.length && !channels.includes(sim.channel_type)) {
        return false;
      }
      if (
        schedules.length &&
        !schedules.includes(scheduleFrequencyToFilterValue(sim.schedule_frequency))
      ) {
        return false;
      }
      return true;
    });
  }, [simulations, filters]);

  return { filters, activeFilterCount, setFilter, removeFilter, resetFilters, filteredSimulations };
};
